import type { ProjectPLBucket } from '../types';
import type { ExpenseLegendRow } from './expenseLegend';

export type InferConfidence = 'high' | 'low' | 'none';

export interface InferExpenseCategoryInput {
  vendorName?: string | null;
  description?: string | null;
  accountName?: string | null;
}

export interface InferExpenseCategoryResult {
  legendId: string | null;
  plBucket: ProjectPLBucket;
  label: string | null;
  confidence: InferConfidence;
  reason: string;
}

const BUCKET_REGEX: Array<{ match: RegExp; plBucket: ProjectPLBucket }> = [
  // Engineering before design: "Engineering Services", PE stamps, structural calcs are their own P&L line.
  { match: /engineering|engineer\b|\bPE\b|stamp|structural calc/i, plBucket: 'engineering' },
  { match: /design|drafting|architect/i, plBucket: 'design' },
  { match: /timber|lumber|wood|material|decking|fastener|peg|hardware|home depot|lowes|menards|mcmaster|grainger/i, plBucket: 'timber' },
  { match: /cnc|shop|production|fabricat/i, plBucket: 'production' },
  { match: /freight|shipping|delivery|trucking|ups|fedex|usps|old dominion|xpo/i, plBucket: 'freight' },
  { match: /install|crane|telehandler|scissor|lift|labor|rental/i, plBucket: 'install' },
  { match: /travel|hotel|lodging|marriott|hilton|hyatt|airbnb|per\s?diem|airfare|fuel|gas|mileage|shell|exxon|pilot flying/i, plBucket: 'travel' },
  { match: /overhead|insurance|admin|office|utilities|software|subscription|saas|adobe|google workspace|microsoft/i, plBucket: 'overhead' },
];

function pickLegendForBucket(rows: ExpenseLegendRow[], plBucket: ProjectPLBucket): ExpenseLegendRow | null {
  return rows.find(r => r.plBucket === plBucket) || null;
}

export function inferExpenseCategory(
  rows: ExpenseLegendRow[],
  input: InferExpenseCategoryInput,
  merchantRules: Record<string, string> = {},
): InferExpenseCategoryResult {
  const vendor = (input.vendorName || '').trim().toLowerCase();
  const desc = (input.description || '').trim().toLowerCase();
  const account = (input.accountName || '').trim().toLowerCase();
  const haystack = `${vendor} ${desc} ${account}`.trim();

  if (vendor && merchantRules[vendor]) {
    const row = rows.find(r => r.id === merchantRules[vendor]);
    if (row) {
      return { legendId: row.id, plBucket: row.plBucket, label: row.label, confidence: 'high', reason: 'merchant_rule' };
    }
  }

  for (const { match, plBucket } of BUCKET_REGEX) {
    if (match.test(haystack)) {
      const row = pickLegendForBucket(rows, plBucket);
      if (row) {
        return {
          legendId: row.id,
          plBucket: row.plBucket,
          label: row.label,
          confidence: vendor || desc ? 'high' : 'low',
          reason: 'regex',
        };
      }
    }
  }

  const fallback = pickLegendForBucket(rows, 'unassigned') || rows[rows.length - 1];
  return {
    legendId: fallback?.id || null,
    plBucket: fallback?.plBucket || 'unassigned',
    label: fallback?.label || 'Other',
    confidence: 'none',
    reason: 'fallback',
  };
}
